/**
 * DNC & Registries Page
 *
 * Full management UI with three sections:
 * 1. Internal DNC (system-managed, read-only)
 * 2. National Registries (CRUD)
 * 3. Frequency Caps (CRUD)
 */
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from '@tanstack/react-router';
import { Card, CardContent, Badge, Button } from '@respectly/ui';
import {
  ShieldBan,
  ArrowLeft,
  Plus,
  Loader2,
  Shield,
  Globe,
  Timer,
  Hash,
  Phone,
  Globe2,
  ChevronRight,
} from 'lucide-react';
import type { DNCRegistry, FrequencyCapConfig } from '@respectly/shared-types';
import { usePolicyStore } from '../../stores/usePolicyStore';
import { useInternalDncStore } from '../../stores/useInternalDncStore';
import { DNCRegistryCard } from '../../components/policy/DNCRegistryCard';
import { DNCRegistryForm } from '../../components/policy/DNCRegistryForm';
import { FrequencyCapCard } from '../../components/policy/FrequencyCapCard';
import { FrequencyCapForm } from '../../components/policy/FrequencyCapForm';

// ============================================
// Animation Variants
// ============================================

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

const cardListVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.06 },
  },
};

const cardItemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.25 } },
};

// ============================================
// Component
// ============================================

export function PolicyDNC() {
  const dncRegistries = usePolicyStore((s) => s.dncRegistries);
  const frequencyCaps = usePolicyStore((s) => s.frequencyCaps);
  const jurisdictions = usePolicyStore((s) => s.jurisdictions);
  const isLoadingDncRegistries = usePolicyStore((s) => s.isLoadingDncRegistries);
  const isLoadingFrequencyCaps = usePolicyStore((s) => s.isLoadingFrequencyCaps);
  const loadDncRegistries = usePolicyStore((s) => s.loadDncRegistries);
  const loadFrequencyCaps = usePolicyStore((s) => s.loadFrequencyCaps);
  const loadJurisdictions = usePolicyStore((s) => s.loadJurisdictions);
  const updateDncRegistry = usePolicyStore((s) => s.updateDncRegistry);
  const deleteDncRegistry = usePolicyStore((s) => s.deleteDncRegistry);
  const updateFrequencyCap = usePolicyStore((s) => s.updateFrequencyCap);
  const deleteFrequencyCap = usePolicyStore((s) => s.deleteFrequencyCap);

  const internalDncConfig = useInternalDncStore((s) => s.config);
  const loadInternalDncConfig = useInternalDncStore((s) => s.loadConfig);

  const [registryFormOpen, setRegistryFormOpen] = useState(false);
  const [editingRegistry, setEditingRegistry] = useState<DNCRegistry | null>(null);
  const [capFormOpen, setCapFormOpen] = useState(false);
  const [editingCap, setEditingCap] = useState<FrequencyCapConfig | null>(null);

  useEffect(() => {
    loadDncRegistries();
    loadFrequencyCaps();
    loadJurisdictions();
    loadInternalDncConfig();
  }, [loadDncRegistries, loadFrequencyCaps, loadJurisdictions, loadInternalDncConfig]);

  const internalDncEnabled = internalDncConfig?.enabled ?? false;
  const areaCodeCount = internalDncConfig?.rules.areaCodes.length ?? 0;
  const exactNumberCount = internalDncConfig?.rules.exactNumbers.length ?? 0;
  const prefixCount = internalDncConfig?.rules.prefixes.length ?? 0;
  const totalRuleCount = areaCodeCount + exactNumberCount + prefixCount;

  const isLoading = isLoadingDncRegistries || isLoadingFrequencyCaps;

  // Registry handlers
  const handleEditRegistry = (r: DNCRegistry) => {
    setEditingRegistry(r);
    setRegistryFormOpen(true);
  };

  const handleDeleteRegistry = async (r: DNCRegistry) => {
    if (!window.confirm(`Delete DNC registry "${r.name}"?`)) return;
    await deleteDncRegistry(r.id);
  };

  const handleToggleRegistry = async (r: DNCRegistry) => {
    await updateDncRegistry(r.id, { isActive: !r.isActive });
  };

  const handleAddRegistry = () => {
    setEditingRegistry(null);
    setRegistryFormOpen(true);
  };

  const handleRegistryFormClose = () => {
    setRegistryFormOpen(false);
    setEditingRegistry(null);
  };

  // Frequency cap handlers
  const handleEditCap = (c: FrequencyCapConfig) => {
    setEditingCap(c);
    setCapFormOpen(true);
  };

  const handleDeleteCap = async (c: FrequencyCapConfig) => {
    if (!window.confirm(`Delete frequency cap "${c.name}"?`)) return;
    await deleteFrequencyCap(c.id);
  };

  const handleToggleCap = async (c: FrequencyCapConfig) => {
    await updateFrequencyCap(c.id, { isActive: !c.isActive });
  };

  const handleAddCap = () => {
    setEditingCap(null);
    setCapFormOpen(true);
  };

  const handleCapFormClose = () => {
    setCapFormOpen(false);
    setEditingCap(null);
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="flex flex-col h-full"
    >
      {/* Header */}
      <motion.div
        variants={itemVariants}
        className="flex-shrink-0 px-6 py-6 border-b border-slate-200 dark:border-slate-700"
      >
        <div className="flex items-center gap-4">
          <Link
            to="/settings/policy"
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-slate-500" />
          </Link>
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-rose-100 to-rose-200 dark:from-rose-900/40 dark:to-rose-800/40 flex items-center justify-center">
            <ShieldBan className="w-6 h-6 text-rose-600 dark:text-rose-400" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
              DNC & Registries
            </h1>
            <p className="text-slate-500 dark:text-slate-400">
              Manage Do-Not-Contact registries, scrub schedules, and frequency caps
            </p>
          </div>
        </div>
      </motion.div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-rose-500" />
          </div>
        ) : (
          <div className="space-y-10 max-w-6xl">
            {/* ============================================ */}
            {/* Section 1 — Internal DNC */}
            {/* ============================================ */}
            <motion.div variants={itemVariants}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-7 h-7 rounded-lg bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center">
                  <Shield className="w-4 h-4 text-rose-600 dark:text-rose-400" />
                </div>
                <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-700 dark:text-slate-300">
                  Internal DNC
                </h2>
              </div>

              <Card className="border-slate-200/60 dark:border-slate-700/60">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="space-y-3 flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-900 dark:text-white">
                          Platform Internal Do-Not-Contact List
                        </span>
                        <Badge
                          variant={internalDncEnabled ? 'success' : 'secondary'}
                          className="text-[10px]"
                        >
                          {internalDncEnabled ? 'Active' : 'Disabled'}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xl">
                        Platform-wide hard stop. Numbers matching an Internal DNC rule are
                        blocked from outbound SMS — PRE evaluation still runs and is logged,
                        only the provider call is skipped.
                      </p>
                      <div className="flex items-center gap-4 flex-wrap">
                        <Badge variant="outline" className="font-mono text-[10px]">
                          PRE-GEN-002
                        </Badge>
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                          {internalDncEnabled
                            ? 'Enforcement on — matching numbers are blocked'
                            : 'Enforcement off — rules configured but not enforced'}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 flex-wrap text-sm text-slate-600 dark:text-slate-300">
                        <span className="inline-flex items-center gap-1.5">
                          <ShieldBan className="w-4 h-4 text-rose-400" />
                          <span className="font-medium tabular-nums">{totalRuleCount}</span>
                          <span className="text-slate-500 dark:text-slate-400">
                            rule{totalRuleCount === 1 ? '' : 's'}
                          </span>
                        </span>
                        {areaCodeCount > 0 && (
                          <span className="inline-flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                            <Hash className="w-3 h-3" />
                            <span className="tabular-nums">{areaCodeCount}</span> area code{areaCodeCount === 1 ? '' : 's'}
                          </span>
                        )}
                        {exactNumberCount > 0 && (
                          <span className="inline-flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                            <Phone className="w-3 h-3" />
                            <span className="tabular-nums">{exactNumberCount}</span> number{exactNumberCount === 1 ? '' : 's'}
                          </span>
                        )}
                        {prefixCount > 0 && (
                          <span className="inline-flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                            <Globe2 className="w-3 h-3" />
                            <span className="tabular-nums">{prefixCount}</span> prefix{prefixCount === 1 ? '' : 'es'}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-lg bg-rose-50 dark:bg-rose-900/20 flex items-center justify-center">
                        <ShieldBan className="w-5 h-5 text-rose-500 dark:text-rose-400" />
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 pt-4 border-t border-slate-200/60 dark:border-slate-700/60 flex justify-end">
                    <Link to="/settings/policy/dnc/internal">
                      <Button size="sm" className="gap-1.5">
                        Manage Internal DNC
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* ============================================ */}
            {/* Section 2 — National Registries */}
            {/* ============================================ */}
            <motion.div variants={itemVariants}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-lg bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center">
                    <Globe className="w-4 h-4 text-rose-600 dark:text-rose-400" />
                  </div>
                  <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-700 dark:text-slate-300">
                    National Registries
                  </h2>
                  <Badge variant="secondary" className="text-[10px] tabular-nums">
                    {dncRegistries.length}
                  </Badge>
                </div>
                <Button size="sm" onClick={handleAddRegistry}>
                  <Plus className="w-4 h-4 mr-1.5" />
                  Add Registry
                </Button>
              </div>

              {dncRegistries.length === 0 ? (
                <Card className="border-dashed border-2 border-slate-200 dark:border-slate-700">
                  <CardContent className="p-8 text-center">
                    <div className="w-12 h-12 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-3">
                      <Globe className="w-6 h-6 text-slate-400" />
                    </div>
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-1">
                      No national registries
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                      Add national DNC registries to enforce scrub requirements.
                    </p>
                    <Button size="sm" onClick={handleAddRegistry}>
                      <Plus className="w-4 h-4 mr-1.5" />
                      Add Registry
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <motion.div
                  variants={cardListVariants}
                  initial="hidden"
                  animate="visible"
                  className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4"
                >
                  {dncRegistries.map((r) => (
                    <motion.div key={r.id} variants={cardItemVariants}>
                      <DNCRegistryCard
                        registry={r}
                        onEdit={handleEditRegistry}
                        onDelete={handleDeleteRegistry}
                        onToggle={handleToggleRegistry}
                      />
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </motion.div>

            {/* ============================================ */}
            {/* Section 3 — Frequency Caps */}
            {/* ============================================ */}
            <motion.div variants={itemVariants}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-lg bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center">
                    <Timer className="w-4 h-4 text-rose-600 dark:text-rose-400" />
                  </div>
                  <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-700 dark:text-slate-300">
                    Frequency Caps
                  </h2>
                  <Badge variant="secondary" className="text-[10px] tabular-nums">
                    {frequencyCaps.length}
                  </Badge>
                </div>
                <Button size="sm" onClick={handleAddCap}>
                  <Plus className="w-4 h-4 mr-1.5" />
                  Add Frequency Cap
                </Button>
              </div>

              {frequencyCaps.length === 0 ? (
                <Card className="border-dashed border-2 border-slate-200 dark:border-slate-700">
                  <CardContent className="p-8 text-center">
                    <div className="w-12 h-12 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-3">
                      <Timer className="w-6 h-6 text-slate-400" />
                    </div>
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-1">
                      No frequency caps
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                      Add frequency cap rules to limit contact attempts.
                    </p>
                    <Button size="sm" onClick={handleAddCap}>
                      <Plus className="w-4 h-4 mr-1.5" />
                      Add Frequency Cap
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <motion.div
                  variants={cardListVariants}
                  initial="hidden"
                  animate="visible"
                  className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4"
                >
                  {frequencyCaps.map((c) => (
                    <motion.div key={c.id} variants={cardItemVariants}>
                      <FrequencyCapCard
                        cap={c}
                        onEdit={handleEditCap}
                        onDelete={handleDeleteCap}
                        onToggle={handleToggleCap}
                      />
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </motion.div>
          </div>
        )}
      </div>

      {/* Modals */}
      <DNCRegistryForm
        isOpen={registryFormOpen}
        onClose={handleRegistryFormClose}
        registry={editingRegistry}
      />
      <FrequencyCapForm
        isOpen={capFormOpen}
        onClose={handleCapFormClose}
        cap={editingCap}
        jurisdictions={jurisdictions}
      />
    </motion.div>
  );
}
